import {
  $createListItemNode,
  $createListNode,
  ListItemNode,
  ListNode,
} from '@lexical/list';
import {
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $createParagraphNode,
  ElementNode,
  RangeSelection,
  TextNode,
  $createTextNode,
  $getRoot,
} from 'lexical';
import { EditorListTypes } from 'src/types';

const handleBackspaceAfterTable = (anchorNode: ElementNode | TextNode) => {
  if (!$isParagraphNode(anchorNode) || anchorNode.getTextContent() !== '') {
    return false;
  }

  const prevSibling = anchorNode.getPreviousSibling();

  if (prevSibling?.getType() === 'table') {
    prevSibling.remove();
    return true;
  }

  return false;
};

const handleBackspaceInList = (
  selection: RangeSelection,
  anchorNode: ElementNode | TextNode,
  event: KeyboardEvent,
) => {
  const listItemNode = anchorNode.getParent();
  if (!(listItemNode instanceof ListItemNode)) {
    return false;
  }

  const listNode = listItemNode.getParent();
  if (!(listNode instanceof ListNode)) {
    return false;
  }

  const isEmpty = anchorNode.getTextContent() === '';
  const isOnlyItem = listNode.getChildrenSize() === 1;

  if (isEmpty) {
    listItemNode.remove();

    if (isOnlyItem) {
      listNode.remove();

      const paragraph = $createParagraphNode();
      selection.insertNodes([paragraph]);
      paragraph.select();

      event.preventDefault();

      return true;
    }

    return true;
  }

  return false;
};

export const registerKeyDownCommand = (event: KeyboardEvent) => {
  if (event.key === 'Backspace') {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();

      if (handleBackspaceAfterTable(anchorNode)) {
        return true;
      }

      if (handleBackspaceInList(selection, anchorNode, event)) {
        return true;
      }
    }
  }

  return false;
};

const getEnclosingList = (sel: RangeSelection): ListNode | null => {
  let node: ElementNode | TextNode | null = sel.anchor.getNode();

  while (node && !(node instanceof ListNode)) {
    node = node.getParent();
  }

  return node as ListNode | null;
};

// (irina) unwraps list items from the list and puts them back into the editor
const unwrapList = (list: ListNode) => {
  list.getChildren().forEach((li) => {
    if (li instanceof ListItemNode) {
      const p = $createParagraphNode();
      p.append(...li.getChildren());
      list.insertBefore(p);
    }
  });
  list.remove();
};

// (irina) wraps selected text into a list (bullet or numbered)
const wrapSelectionIntoList = (
  selection: RangeSelection,
  type: EditorListTypes,
): void => {
  const anchorNode = selection.anchor.getNode();
  let anchorBlock: ElementNode;

  try {
    anchorBlock = anchorNode.getTopLevelElementOrThrow();
  } catch {
    // (irina) if document empty, create a new paragraph
    const paragraph = $createParagraphNode();
    const text = $createTextNode('\u200B');
    paragraph.append(text);
    const root = $getRoot();
    root.append(paragraph);
    paragraph.select();
    anchorBlock = paragraph;
  }

  const topBlocks = selection.isCollapsed()
    ? [anchorBlock]
    : Array.from(
        new Set(selection.getNodes().map((n) => n.getTopLevelElementOrThrow())),
      );

  const list = $createListNode(type);
  anchorBlock.insertBefore(list);

  topBlocks.forEach((block) => {
    const li = $createListItemNode();

    if (block instanceof ElementNode) {
      li.append(...block.getChildren());
    }

    if (li.getChildrenSize() === 0) {
      const p = $createParagraphNode();
      p.append($createTextNode('\u200B'));
      li.append(p);
    }

    list.append(li);
    block.remove();
  });

  list.selectEnd();
};

const insertListCommand = (type: EditorListTypes): boolean => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return false;
  }

  const list = getEnclosingList(selection);

  if (list) {
    // if the selection is inside a list
    list.getListType() === type // if the list is the same type (e.g. bullet)
      ? unwrapList(list) // remove list from selected text if it's the same type
      : list.setListType(type); // if the list is different type (e.g. bullet -> number) then change type of the list
  } else {
    wrapSelectionIntoList(selection, type);
  }

  return true;
};

export const insertBulletListCommand = (): boolean => {
  return insertListCommand('bullet');
};

export const insertNumberedListCommand = (): boolean => {
  return insertListCommand('number');
};
